import dayjs from 'dayjs';
import type { Emitter } from 'mitt';
import {
  all,
  call,
  delay,
  put,
  race,
  SagaGenerator,
  take,
} from 'typed-redux-saga';
import { v4 as uuid } from 'uuid';
import {
  type AsyncActionState,
  asyncActionStateMatchers,
  invoke,
  setupSlice,
} from '@owl-frontend/redux';
import claimsApiClient from '../../../api/claims/client';
import type { Claim, Participant } from '../../../api/claims/interface';
import filesApiClient from '../../../api/files/client';
import type { SelfServiceClaimFile } from '../../../api/files/interface';

const prepareFilesForUpload = (files: File[]): SelfServiceClaimFile[] => {
  return files.map((file) => ({
    file_name: file.name,
    file_size: file.size,
    mime_type: file.type,
    received_at: dayjs().format('YYYY-MM-DD'),
  }));
};

export const FETCH_LIMIT = 10;
function* fetchClaims(action: {
  payload: {
    filters: {
      name_or_id?: string;
      cid_date?: [string, string];
      owners?: string[];
      statuses?: Array<'Open' | 'Pending' | 'Closed'>;
    };
    sort: {
      key: string[] | string;
      order: 'ascend' | 'descend';
    } | null;
    currentPage: number;
    pageSize: number;
  };
}): SagaGenerator<{ dossiers: Claim[]; count: number }> {
  const {
    filters: { name_or_id, cid_date, owners, statuses },
    sort,
    currentPage,
    pageSize,
  } = action.payload;
  const transformedCidDate = cid_date?.map((d: string) => dayjs(d));

  const mappedFilters: Array<
    | {
        filter_name: 'name_or_id';
        data: { query: string };
      }
    | {
        filter_name: 'owner';
        data: {
          owners: string[];
        };
      }
    | {
        filter_name: 'status';
        data: {
          statuses: Array<'Open' | 'Pending' | 'Closed'>;
        };
      }
    | {
        column: string;
        json_path: string[];
        operator: string;
        value: string;
      }
    | {
        filter_name?: never;
        column?: never;
      }
  > = [];

  if (name_or_id) {
    mappedFilters.push({
      filter_name: 'name_or_id',
      data: {
        query: name_or_id,
      },
    });
  }

  if (transformedCidDate) {
    const [startDate, endDate] = transformedCidDate;

    mappedFilters.push({
      column: 'data',
      json_path: ['cid_date'],
      operator: '>=',
      value: dayjs(startDate).format('YYYY-MM-DD'),
    });

    mappedFilters.push({
      column: 'data',
      json_path: ['cid_date'],
      operator: '<=',
      value: dayjs(endDate).format('YYYY-MM-DD'),
    });
  }

  if (owners) {
    mappedFilters.push({
      filter_name: 'owner',
      data: {
        owners,
      },
    });
  }

  if (statuses) {
    mappedFilters.push({
      filter_name: 'status',
      data: {
        statuses,
      },
    });
  }

  return yield* call(claimsApiClient.fetch, {
    filters: JSON.stringify(mappedFilters),
    sort: sort
      ? {
          sort_by: sort.key,
          is_descending: sort.order === 'descend',
        }
      : undefined,
    offset: (currentPage - 1) * pageSize,
    limit: pageSize,
  });
}

export type ClaimsListEvents = {
  invalidateClaimsPage: void;
};

export const isProcessing = (status: string) =>
  status !== 'done' &&
  status !== 'error' &&
  status !== 'deleted' &&
  status !== 'new' &&
  status !== 'no_data';

const POLL_INTERVAL_MS = 30_000; // 30 seconds

function* pollClaimsList(action: {
  payload: { bus: Emitter<ClaimsListEvents> };
}) {
  const { bus } = action.payload;
  while (true) {
    yield delay(POLL_INTERVAL_MS);
    bus.emit('invalidateClaimsPage');
  }
}

function* startPollingClaimsList(action: {
  payload: { bus: Emitter<ClaimsListEvents> };
}) {
  yield* put({ type: 'STOP_POLLING_CLAIMS_LIST' });
  yield* race({
    poll: call(pollClaimsList, action),
    stop: take('STOP_POLLING_CLAIMS_LIST'),
  });
}

function* uploadFile(file: File, uploadId: string) {
  try {
    const presigned = yield call(filesApiClient.uploadPresignedUrl, {
      fileName: file.name,
      contentType: file.type,
      uploadId,
    });
    yield call(filesApiClient.upload, {
      ...presigned,
      contentType: file.type,
      file,
    });

    return;
  } catch (uploadError) {
    return file.name;
  }
}

function* uploadFiles(files: File[], uploadId: string) {
  const results: [string | undefined] = yield all(
    files.map((file) => call(uploadFile, file, uploadId))
  );

  // uploadFile returns a file name if the upload failed, undefined otherwise
  const failedFiles = results.filter((result: string | undefined) => {
    return !!result;
  });

  if (failedFiles.length > 0) {
    throw new Error(`Failed to upload files: ${[...failedFiles].join(', ')}`);
  }
}

function* createSelfServiceClaim(action: {
  payload: {
    claimants: Participant[];
    insured?: Participant;
    clientDossierId: string;
    files: File[];
    assignee?: string;
    notes?: string;
    dossierType?: string;
  };
}) {
  const {
    files,
    claimants,
    insured,
    notes,
    assignee,
    clientDossierId,
    dossierType,
  } = action.payload;
  const uploadId = uuid();
  yield* call(uploadFiles, files, uploadId);
  const filesToUpload: SelfServiceClaimFile[] = prepareFilesForUpload(files);
  yield call(claimsApiClient.selfServiceCreate, {
    claimants,
    insured,
    clientDossierId,
    assignee,
    files: filesToUpload,
    notes,
    uploadId,
    dossierType,
  });
}

function* updateClaimFiles(action: {
  payload: {
    dossierId: string;
    files?: File[];
    notes?: string;
  };
}) {
  const { files, notes, dossierId } = action.payload;
  const uploadId = uuid();
  let filesToUpload: SelfServiceClaimFile[] = [];
  if (files) {
    yield* call(uploadFiles, files, uploadId);
    filesToUpload = prepareFilesForUpload(files);
  }

  yield call(claimsApiClient.updateClaim, {
    dossierId,
    files: filesToUpload,
    notes,
    uploadId,
  });
}

function* checkClientDossierIdUnique(action: {
  payload: { clientDossierId: string };
}): SagaGenerator<boolean> {
  const { dossiers } = yield* call(claimsApiClient.fetch, {
    filters: JSON.stringify([
      {
        filter_name: 'name_or_id',
        data: { query: action.payload.clientDossierId },
      },
    ]),
    offset: 0,
    limit: 1,
  });

  return dossiers.length === 0;
}

export interface ClaimsState {
  claims: Claim[];
  currentPage: number;
  count: number;
  isClientDossierIdUnique: boolean;
  actions: {
    deleteClaim: AsyncActionState;
    fetchClaims: AsyncActionState;
    updateClaimStatus: AsyncActionState;
    updateClaimFiles: AsyncActionState;
    createSelfServiceClaim: AsyncActionState;
    startPollingClaimsList: AsyncActionState;
    checkClientDossierIdUnique: AsyncActionState<boolean>;
  };
}

const initialState: ClaimsState = {
  claims: [],
  currentPage: 0,
  count: 0,
  isClientDossierIdUnique: false,
  actions: {
    deleteClaim: { status: 'uninitialized' },
    fetchClaims: { status: 'uninitialized' },
    updateClaimStatus: { status: 'uninitialized' },
    updateClaimFiles: { status: 'uninitialized' },
    createSelfServiceClaim: { status: 'uninitialized' },
    startPollingClaimsList: { status: 'uninitialized' },
    checkClientDossierIdUnique: { status: 'uninitialized' },
  },
};

const slice = setupSlice('claims', initialState)
  .addAsyncSagas({
    fetchClaims,
    updateClaimStatus: invoke(claimsApiClient.updateClaim),
    deleteClaim: invoke(claimsApiClient.deleteClaim),
    checkClientDossierIdUnique,
  })
  .addAsyncSagas(
    {
      createSelfServiceClaim,
      updateClaimFiles,
    },
    {
      timeoutLimit: -1,
    }
  )
  .addAsyncSagas(
    {
      startPollingClaimsList,
    },
    {
      timeoutLimit: -1,
    }
  )
  .addReducers({
    'fetchClaims/fulfilled': (state, action) => {
      state.claims = action.payload.dossiers;
      state.count = action.payload.count;
      state.currentPage = action.meta.arg.currentPage;
    },
    'checkClientDossierIdUnique/fulfilled': (state, action) => {
      state.isClientDossierIdUnique = action.payload;
    },
    'createSelfServiceClaim/fulfilled': (state, _) => {
      state.isClientDossierIdUnique = false;
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
